// Path: src/components/workshoppage/MessageRenderer.tsx
import React from 'react';
import { Maximize2 } from 'lucide-react';
import { type PatchResult } from '../../types/workshop';
import { StreamingCodeBlock } from '../common/StreamingCodeBlock';
import { mergeHtmlFences } from '../../lib/htmlChunkAssembler';

interface FileData {
  oss_url: string;
  file_type: string; // 'image_input' | 'image_output' | 'document'
}

interface Props {
  content?: string;
  files?: FileData[];
  patched_html?: string;
  patch_result?: PatchResult;
  onApplyCode: (code: string) => void;
  // 流式消息相关：
  partial?: boolean;
  partialReason?: string;
}

type ProcessedPart =
  | { type: 'text'; content: string }
  | { type: 'code'; content: string; language: string; isTruncated: boolean };

export const MessageRenderer = ({
  content = '',
  files = [],
  patched_html,
  patch_result,
  onApplyCode,
  partial,
  partialReason,
}: Props) => {
  const safeContent = content || '';

  // 解析内容：完整代码块匹配 + 截断 markdown 检测 + 裸 HTML 检测
  // 确保即使多轮接收 HTML 代码也能完整放入一个 <pre><code></code></pre> 中
  const processContent = (content: string, patchedHtml?: string): ProcessedPart[] => {
    const parts: ProcessedPart[] = [];

    if (patchedHtml) {
      parts.push({ type: 'code', content: patchedHtml, language: 'html', isTruncated: false });
      return parts;
    }

    if (!content) return parts;

    // 合并多轮 HTML 代码围栏，确保渲染为单个 <pre><code> 块
    const mergedContent = mergeHtmlFences(content);

    let lastIndex = 0;
    // 匹配完整代码块 ```lang ... ```
    const completeBlockRegex = /```(\w+)?\s*([\s\S]*?)```/g;
    let match: RegExpExecArray | null;

    while ((match = completeBlockRegex.exec(mergedContent)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: mergedContent.substring(lastIndex, match.index) });
      }
      const lang = match[1] || 'text';
      const code = match[2].trim();
      parts.push({ type: 'code', content: code, language: lang, isTruncated: false });
      lastIndex = completeBlockRegex.lastIndex;
    }

    // 处理剩余文本（检测截断）
    const remaining = mergedContent.substring(lastIndex);
    const unclosedMarkdownMatch = /```(\w+)?\s*([\s\S]*)$/.exec(remaining);
    const rawHtmlStartIdx = remaining.indexOf('<!DOCTYPE html>');
    const isRawHtmlTruncated = rawHtmlStartIdx !== -1 && !remaining.includes('</html>');

    if (unclosedMarkdownMatch) {
      const preText = remaining.substring(0, unclosedMarkdownMatch.index);
      if (preText) parts.push({ type: 'text', content: preText });

      const lang = unclosedMarkdownMatch[1] || 'html';
      const code = unclosedMarkdownMatch[2];
      parts.push({ type: 'code', content: code, language: lang, isTruncated: true });
    } else if (isRawHtmlTruncated) {
      const preText = remaining.substring(0, rawHtmlStartIdx);
      if (preText) parts.push({ type: 'text', content: preText });

      const code = remaining.substring(rawHtmlStartIdx);
      parts.push({ type: 'code', content: code, language: 'html', isTruncated: true });
    } else if (remaining) {
      parts.push({ type: 'text', content: remaining });
    }

    return parts;
  };

  const parts = processContent(safeContent, patched_html);

  return (
    <div className="flex flex-col gap-2">
      {/* 🖼️ 1. 图片渲染区域 */}
      {files && files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {files.map((file, idx) => {
            const isOutput = file.file_type === 'image_output';
            const isVideo = file.oss_url.toLowerCase().endsWith('.mp4');

            return (
              <div key={idx} className={`relative group ${isOutput ? 'w-full' : 'w-24 h-24'}`}>
                {isVideo ? (
                  <div className={`rounded-lg border border-gray-200 flex items-center justify-center ${
                    isOutput ? 'w-full h-auto min-h-[100px]' : 'w-full h-full'
                  } bg-gray-100 relative`}>
                    <a
                      href={file.oss_url}
                      target="_blank"
                      rel="noreferrer"
                      className="absolute inset-0 bg-black/20 hover:bg-black/30 transition-colors flex items-center justify-center rounded-lg"
                      title="在新窗口打开视频"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width={isOutput ? 32 : 20} height={isOutput ? 32 : 20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                      </svg>
                    </a>
                  </div>
                ) : (
                  <>
                    <img
                      src={file.oss_url}
                      alt="attachment"
                      className={`rounded-lg border border-gray-200 object-cover ${
                        isOutput ? 'w-full h-auto max-h-[300px]' : 'w-full h-full'
                      }`}
                    />
                    <a
                      href={file.oss_url}
                      target="_blank"
                      rel="noreferrer"
                      className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center rounded-lg"
                    >
                      <Maximize2 className="text-white opacity-0 group-hover:opacity-100" size={isOutput ? 24 : 16} />
                    </a>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 📝 3. 文本/代码渲染区域 */}
      <div>
        {parts.map((part, index) => {
          if (part.type === 'code') {
            return (
              <StreamingCodeBlock
                key={index}
                code={part.content}
                language={part.language}
                isTruncated={part.isTruncated}
                theme="light"
                onApplyCode={part.language === 'html' ? onApplyCode : undefined}
              />
            );
          }
          // 普通文本
          return <span key={index} className="whitespace-pre-wrap">{part.content}</span>;
        })}
      </div>

      {/* ⚠️ 4. 内容不完整提示（meta.partial === true） */}
      {partial === true && (
        <div className="mt-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700 flex items-center gap-2">
          <span>⚠️</span>
          <span>内容可能不完整{partialReason ? `（${partialReason}）` : ''}</span>
        </div>
      )}
    </div>
  );
};
